import { filterClasses } from "@swivl-mono/helpers";
import { InheritanceStatus } from "@swivl-mono/types";
import { RadioButtons, SearchInput, Select, SelectItem, SelectSize, useOutsideClick } from "@swivl-mono/ui";
import React from "react";

export interface WorkflowsNavSearchInterface {
  searchQuery: string;
  searchType: "quest" | "node";
  inheritanceFilter: InheritanceStatus | "ALL";
  isSearching?: boolean;
}

interface WorkflowsNavSearchProps {
  isChild: boolean;
  searchUpdated: (searchData: WorkflowsNavSearchInterface) => void;
  data: WorkflowsNavSearchInterface;
  isLoading?: boolean;
}

export function WorkflowsNavSearch(props: WorkflowsNavSearchProps) {
  const { data, isChild, searchUpdated, isLoading } = props;
  const [isEditing, setIsEditing] = React.useState(false);
  const ref = useOutsideClick(() => {
    setIsEditing(false);
  });

  const shouldBeOpen = isEditing || data.inheritanceFilter !== "ALL";

  return (
    <div ref={ref} className="relative z-500 ml-3 h-[50px] w-[300px]">
      <div className="flex h-[50px] items-center px-[10px]">
        <SearchInput
          containerClassName="m-0 w-[280px]"
          onFocus={() => {
            setIsEditing(true);
          }}
          onChange={(e) => {
            searchUpdated({ ...data, searchQuery: e.target.value });
          }}
          value={data.searchQuery}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              setIsEditing(false);
            }
          }}
          onClear={() => {
            searchUpdated({ ...data, searchQuery: "" });
          }}
          placeholder={data.searchType === "quest" ? "Search Quests..." : "Search Nodes..."}
        />
      </div>

      <div
        className={filterClasses(
          "absolute top-11 right-0 left-0 z-600 px-[10px] pt-1 transition-all duration-200 ease-in-out",
          shouldBeOpen ? "pointer-events-auto translate-y-0 opacity-100" : "pointer-events-none -translate-y-1 opacity-0"
        )}
      >
        <div className="rounded-[5px] bg-grey-50 px-0 shadow-[0px_3px_8px_rgba(0,0,0,0.25)]">
          <div className="flex h-[45px] flex-row items-center gap-2.5 overflow-visible px-2 py-[5px]">
            <RadioButtons
              inline={true}
              value={data.searchType}
              options={[
                { label: "Quests", value: "quest" },
                { label: "Nodes", value: "node" }
              ]}
              onOptionSelected={(option) => {
                searchUpdated({ ...data, searchType: option.value });
              }}
            />

            {isChild && (
              <div className="w-[130px]">
                <Select
                  size={SelectSize.SMALL}
                  hideClear
                  selectedValues={[data.inheritanceFilter]}
                  options={
                    [
                      { label: "ALL", value: "ALL" },
                      { label: "STANDALONE", value: InheritanceStatus.NONE },
                      { label: "CHILD", value: InheritanceStatus.IS_CHILD },
                      { label: "PARENT", value: InheritanceStatus.IS_PARENT }
                    ] as SelectItem[]
                  }
                  onChange={(vals) => {
                    searchUpdated({ ...data, inheritanceFilter: (vals[0] as InheritanceStatus | "ALL") ?? "ALL" });
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
